import { useEffect, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useMeQuery } from 'core/graphql';
import { accessSuspendedVar, authState } from 'core/state';

import { ILmsAccess, transformLmsAccess } from './transform';

/** Как часто приостановленный студент перечитывает `me`, ожидая возврата доступа. */
export const SUSPENSION_POLL_MS = 60 * 1000;

export interface IUseLmsAccess extends ILmsAccess {
  /**
   * Доступ студента к LMS приостановлен (ТЗ 0074): по `me` ИЛИ по ошибке
   * `LMS_ACCESS_SUSPENDED`, пришедшей раньше свежего `me`.
   */
  isSuspended: boolean;
  /** Ответ `me` уже есть (или запрос упал/пропущен) — ждать больше нечего. */
  isResolved: boolean;
}

interface IUseLmsAccessOptions {
  skip?: boolean;
  /**
   * Опрашивать `me`, пока доступ приостановлен. Включает только гард
   * маршрутов: он смонтирован один раз на всё приложение, и возврат доступа
   * (оплату зачёл робот) должен отпустить студента без перезагрузки.
   */
  pollWhileSuspended?: boolean;
}

/**
 * Счётчики неоплаченных фактур и приостановка доступа из `me` (ТЗ 0074).
 * `cache-first`: `me` и так перечитывают шапка и опросные хуки, отдельный
 * сетевой запрос на каждый потребитель не нужен.
 */
export const useLmsAccess = ({
  skip = false,
  pollWhileSuspended = false,
}: IUseLmsAccessOptions = {}): IUseLmsAccess => {
  const { active_role } = useReactiveVar(authState);
  const suspendedByError = useReactiveVar(accessSuspendedVar);

  const { data, error, startPolling, stopPolling } = useMeQuery({
    fetchPolicy: 'cache-first',
    skip,
  });

  const access = useMemo(
    () => transformLmsAccess(data?.me, active_role ?? null),
    [data?.me, active_role],
  );

  const isSuspended = access.isBlocked || (suspendedByError && access.isStudent);

  useEffect(() => {
    if (!pollWhileSuspended || skip || !isSuspended) {
      return undefined;
    }
    startPolling(SUSPENSION_POLL_MS);
    return () => stopPolling();
  }, [pollWhileSuspended, skip, isSuspended, startPolling, stopPolling]);

  return {
    ...access,
    isSuspended,
    isResolved: skip || Boolean(data?.me) || Boolean(error),
  };
};
